import { useEffect } from 'react';

import constants from 'constants/constants';
import moment from 'moment-timezone';
import { WidgetViewMode, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useEventWithDimensions from 'hooks/useEventWithDimensions';

import { useThemeStatusStore } from 'store/themeStatus.store';

import { useUpdateIfReady } from 'components/Dashboards/Reporting/widgetInReport/hooks/RefreshWidgetPayload/useUpdateIfReady';
import { UseProductivityRateProvider } from 'components/widgets/RunRateChart/productivityRate.provider';
import { categoriesKeys, getChartConfig, serialize } from 'components/widgets/RunRateChart/productivityRate.utils';

interface Props {
    ui: UseProductivityRateProvider['ui'];
    api: UseProductivityRateProvider['api'];
    title: string;
    subTitle: string;
    latestVersionId: number | undefined;
    widgetViewMode: WidgetViewMode;
    setIsPayloadReady?: (isPayloadReady: boolean) => void;
}

const CHART_HEIGHT_IN_REPORT_VIEW = '100%';
const CHART_HEIGHT_IN_PAGE_VIEW = 600;

export const useProductivityRateLogic = ({
    ui,
    api,
    title,
    subTitle,
    latestVersionId,
    widgetViewMode,
    setIsPayloadReady,
}: Props) => {
    const isPayloadReady = () => {
        const currentPayload = api.getProductivityRateData.payload;
        if (!currentPayload) return;
        if (currentPayload.actualMetaDataId === undefined || currentPayload.comparedMetaDataId === undefined) {
            return;
        }
        setIsPayloadReady && setIsPayloadReady(true);
    };

    const { sendEventWithDimensions } = useEventWithDimensions();
    const { handleSnackBar } = useCustomSnackBar();
    const { themeStatus } = useThemeStatusStore();
    useUpdateIfReady({ isPayloadReadyCallback: isPayloadReady, widgetViewMode: widgetViewMode });

    useEffect(() => {
        if (api.getProductivityRateData.instance.data) {
            ui.setters.updateProductivityRateStore({
                selectedStartDate: api.getProductivityRateData.instance.data.startChartDate,
                selectedEndDate: api.getProductivityRateData.instance.data.endChartDate,
            });
        }
    }, [api.getProductivityRateData.instance.data]);

    const dataPointSelection = ({ dataPointIndex, seriesIndex }: { dataPointIndex: number; seriesIndex: number }) => {
        const isToggle =
            dataPointIndex === ui.state.dataPointIndexFromStore && seriesIndex === ui.state.seriesIndexFromStore;
        const series = categoriesKeys[seriesIndex].label;
        const value = categoriesKeys[seriesIndex].key;
        if (api.getProductivityRateData.instance.data?.chart) {
            ui.setters.updateProductivityRateStore({
                selectedValue: isToggle ? null : value,
                selectedSeries: isToggle ? null : series,
                selectedStartDate: isToggle
                    ? api.getProductivityRateData?.instance.data?.startChartDate
                    : api.getProductivityRateData?.instance.data.chart[dataPointIndex]?.startDate,
                selectedEndDate: isToggle
                    ? api.getProductivityRateData.instance.data?.endChartDate
                    : api.getProductivityRateData.instance.data?.chart[dataPointIndex]?.endDate,
                dataPointIndex: isToggle ? null : dataPointIndex,
                seriesIndex: isToggle ? null : seriesIndex,
            });
        }
    };

    const tooltipFormatter = ({ dataPointIndex }: { dataPointIndex: number }) => {
        if (
            dataPointIndex === undefined ||
            !api.getProductivityRateData.instance.data ||
            !api.getProductivityRateData.instance.data.chart
        )
            return '';
        const { startDate, endDate } = api.getProductivityRateData.instance.data.chart[dataPointIndex];
        return `${moment(startDate).format(constants.formats.date.default)} - ${moment(endDate).format(constants.formats.date.default)}`;
    };

    const { options, series } = getChartConfig({
        ...serialize({ data: api.getProductivityRateData.instance.data?.chart, range: ui.state?.dateRangeFilter }),
        themeStatus: themeStatus,
        versionDate: api.getProductivityRateData.instance.data?.calculatedVersionDate,
        dataPointSelection,
        tooltipFormatter,
        actualKpi: api.getProductivityRateData.instance.data?.pastActual?.activities,
    });

    const handleAPICsvExport = () => {
        if (latestVersionId) {
            api.exportToCsvRunRateAnalysis.instance.mutate(
                { ...api.exportToCsvRunRateAnalysis.payload, fileName: `${title}_${subTitle}` },
                {
                    onSuccess: () => {
                        handleSnackBar('File Successfully Downloaded', 'success');

                        sendEventWithDimensions({
                            category: 'Export',
                            action: 'CSV',
                            label: `${title} ${subTitle}`,
                        });
                    },
                    onError: (error) => {
                        DEFAULT_ERROR({ error, handleError: handleSnackBar });
                    },
                },
            );
        }
    };

    const chartHeight = WidgetViewModeUtils.isReportView(widgetViewMode)
        ? CHART_HEIGHT_IN_REPORT_VIEW
        : CHART_HEIGHT_IN_PAGE_VIEW;

    return {
        logic: {
            options,
            series,
            handleAPICsvExport,
            chartHeight,
        },
    };
};
